import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Footer from "../components/Layout/Footer";
import { PublicHeader } from "../components/Layout/PublicHeader";
import DividerMotif from "../components/Brand/DividerMotif";
import ScrollReveal from "../components/ui/ScrollReveal";
import { useDocumentMeta } from "../lib/useDocumentMeta";

/*
 * Velaigal /about page.
 *
 * Distinctive moment: the timeline is the page. Each year (2026, Early 2026,
 * 08/2026, Now) renders as massive Fraunces numerals at clamp(120px, 14vw,
 * 200px) in ink-stroke-only outlined type. The years dominate the section,
 * the captions sit small alongside. The timeline is no longer a quiet
 * decorative band; it becomes the editorial subject that visitors remember.
 *
 * Other sections stay quiet to give the timeline room to breathe.
 */

const VALUES = [
  {
    title: "Innovation that earns its keep",
    description:
      "We push AI where it actually changes outcomes, not where it looks impressive in a deck.",
  },
  {
    title: "Human-led, AI-assisted",
    description:
      "Hiring is a human decision. Velaigal makes the inputs honest; the call stays yours.",
  },
  {
    title: "Quality before scale",
    description:
      "We would rather get one assessment right than ship ten that look good in screenshots.",
  },
  {
    title: "Inclusion as a default",
    description:
      "When the work speaks instead of the pedigree, the pipeline gets wider on its own.",
  },
];

const TIMELINE = [
  {
    year: "2026",
    label: "Founded",
    title: "Started with a single thesis",
    description:
      "Hiring runs on dishonest inputs. Resumes get written for keywords, not for what someone can actually do. Velaigal started as a bet that AI could close that gap.",
  },
  {
    year: "Early 2026",
    label: "First build",
    title: "Learned the model had to change",
    description:
      "The first version of Velaigal proved candidates could be evaluated with AI. Working with early hiring teams made one thing clear: the evaluation itself needed to move from answering questions to doing real work.",
  },
  {
    year: "08/2026",
    label: "Rebuilt",
    title: "Moved to task-based hiring",
    description:
      "Velaigal was rebuilt end to end. Post a job, AI drafts real tasks from the JD, candidates submit actual work, a hosted link, a repo, a document, a video, and that submission is the application. Self-hosted, and free during early access.",
  },
  {
    year: "Now",
    label: "Where we are",
    title: "Early, India-first, building in the open",
    description:
      "Velaigal is early-stage and India-first, built and run by one person. The task-submission model is still young, and it gets sharper with every hiring team that tries it.",
  },
];

const TEAM = [
  { name: "Manoj", role: "Founder, Full Stack Developer" },
];

const About: React.FC = () => {
  useDocumentMeta(
    "About · Velaigal",
    "Velaigal is hiring built on demonstrated work, not resumes alone. Read why every hire runs on real tasks, real submissions, and a recruiter who reviews the evidence."
  );

  return (
    <div className="min-h-screen w-full bg-cream text-ink">
      <PublicHeader />

      {/* ---------------- Opening / mission ---------------- */}
      <section className="relative max-w-7xl mx-auto px-8 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-2">
          <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
            About
          </span>
        </div>
        <div className="lg:col-span-10">
          <h1
            className="font-display text-4xl lg:text-6xl font-medium text-ink leading-tight tracking-tight max-w-4xl velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            We believe the resume is the least honest part of hiring. So we
            built something to stand alongside it.
          </h1>
          <span aria-hidden="true" className="block w-12 h-px bg-saffron mt-6" />
          <p className="mt-10 text-lg lg:text-xl text-ink-secondary max-w-2xl leading-relaxed">
            Velaigal is hiring built on what people can actually do. Post a
            job and our AI turns it into real tasks; candidates submit real
            work, resume attached, and that submission is the application.
            We flag what looks strong and what needs a closer look, and hand
            recruiters the evidence, not a verdict. The best person for the
            role almost never has the best CV. We are fixing that.
          </p>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- What we believe ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-20">
          <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
            What we believe
          </span>
          <div className="mt-12 divide-y divide-hairline">
            {VALUES.map((value, idx) => (
              <ScrollReveal
                key={value.title}
                as="article"
                stagger={(Math.min(idx + 1, 4) as 1 | 2 | 3 | 4)}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-10 first:pt-0"
              >
                <div className="lg:col-span-2">
                  <span className="text-[11px] uppercase tracking-widest text-hairline-strong font-medium">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3
                  className="lg:col-span-4 font-display text-2xl font-medium text-ink leading-snug velaigal-headline-hover"
                  style={{
                    fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
                  }}
                >
                  {value.title}
                </h3>
                <p className="lg:col-span-6 text-base text-ink-secondary leading-relaxed">
                  {value.description}
                </p>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- The distinctive moment: years as hero ----------
       *
       * Each year renders as massive outlined Fraunces numerals — the
       * editorial subject of the section. Captions sit small alongside.
       * The years are deliberately oversized; they sweep up the page's
       * memorability budget.
       */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24">
          <div className="flex items-baseline justify-between flex-wrap gap-4">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              How we got here
            </span>
            <span className="text-[11px] uppercase tracking-widest text-hairline-strong font-medium">
              Four marks · 2026 · always improving
            </span>
          </div>

          <h2
            className="sr-only"
          >
            How we got here
          </h2>

          <div className="mt-20 space-y-24 lg:space-y-32">
            {TIMELINE.map((item, idx) => (
              <ScrollReveal
                key={item.year + item.title}
                as="article"
                className="grid grid-cols-1 lg:grid-cols-12 gap-y-8 lg:gap-x-12 items-start"
              >
                {/* Year as hero — the typographic subject */}
                <div className="lg:col-span-7">
                  <div className="overflow-hidden">
                    <span
                      className="font-display block leading-[0.9] text-ink select-none whitespace-nowrap"
                      style={{
                        fontSize: "clamp(72px, 13vw, 184px)",
                        fontVariationSettings:
                          '"opsz" 144, "SOFT" 50, "WONK" 0',
                        fontWeight: 300,
                        letterSpacing: "-0.04em",
                        WebkitTextStroke: "1px var(--color-ink)",
                        WebkitTextFillColor: "transparent",
                        color: "transparent",
                      }}
                    >
                      {item.year}
                    </span>
                  </div>
                </div>

                {/* Editorial caption block */}
                <div className="lg:col-span-5 lg:pt-6">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-sm text-ink-tertiary tabular-nums">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span aria-hidden="true" className="block w-6 h-px bg-saffron" />
                    <span className="text-[11px] uppercase tracking-widest text-saffron font-medium">
                      {item.label}
                    </span>
                  </div>
                  <h3
                    className="mt-5 font-display text-2xl lg:text-3xl font-medium text-ink leading-snug velaigal-headline-hover"
                    style={{
                      fontVariationSettings:
                        '"opsz" 144, "SOFT" 50, "WONK" 0',
                    }}
                  >
                    {item.title}
                  </h3>
                  <p className="mt-4 text-base text-ink-secondary leading-relaxed max-w-md">
                    {item.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- The team ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-20">
          <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
            The team
          </span>
          <h2
            className="font-display text-3xl lg:text-4xl font-medium text-ink leading-tight mt-6 max-w-2xl velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            A team of one, working close to every customer.
          </h2>
          {/*
           * Single-founder layout. The grid-of-cards-with-hairline-gap pattern
           * needs at least two cards to read as a system; with one, an
           * editorial inset card flanked by a Tamil glyph reads as
           * intentional rather than sparse.
           */}
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {TEAM.map((member) => (
              <article
                key={member.name}
                className="lg:col-span-5 relative bg-paper border border-hairline rounded-md p-10 before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:bg-saffron"
              >
                <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                  Founder
                </span>
                <h3
                  className="mt-4 font-display text-3xl font-medium text-ink leading-snug"
                  style={{
                    fontVariationSettings:
                      '"opsz" 144, "SOFT" 50, "WONK" 0',
                  }}
                >
                  {member.name}
                </h3>
                <p className="mt-3 text-[11px] uppercase tracking-widest text-saffron font-medium">
                  {member.role}
                </p>
                <p className="mt-6 text-sm text-ink-secondary leading-relaxed max-w-sm">
                  Builds and ships every part of Velaigal — the platform, the
                  AI pipelines, the hiring loop, and the customer relationships
                  behind them.
                </p>
              </article>
            ))}

            <div className="hidden lg:flex lg:col-span-7 items-end justify-end relative min-h-[260px]">
              <span
                aria-hidden="true"
                className="select-none pointer-events-none leading-none"
                style={{
                  fontFamily: '"Noto Serif Tamil", Georgia, serif',
                  fontSize: "clamp(160px, 16vw, 240px)",
                  color: "var(--color-tamil-mark)",
                  fontWeight: 500,
                  letterSpacing: "-0.04em",
                  opacity: 0.55,
                }}
              >
                வே
              </span>
            </div>
          </div>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- CTA ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24 text-center">
          <h2
            className="font-display text-4xl lg:text-5xl font-medium text-ink leading-tight tracking-tight max-w-3xl mx-auto velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Want to talk?
          </h2>
          <span aria-hidden="true" className="block w-12 h-px bg-saffron mx-auto mt-6" />
          <p className="mt-6 text-base text-ink-secondary max-w-xl mx-auto">
            We answer our own emails. Pilots, partnerships, press, all the
            same inbox.
          </p>
          <div className="mt-10">
            <Link
              to="/contact"
              className="btn-primary no-underline"
              style={{ textDecoration: "none" }}
            >
              Get in touch
              <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
